import { HttpErrorResponse } from '@angular/common/http';
import {
	AsyncSubject,
	BehaviorSubject,
	firstValueFrom,
	NEVER,
	Observable,
	of,
	PartialObserver,
	Subject,
	throwError,
	timer,
} from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { isArray } from './compare';
import { SessionLogger } from './logging/session-logger';
import { Result } from './result';

export interface BehaviorCacheSet<T> {
	data: BehaviorSubject<Result<T>>;
	lastRefresh: Date | null;
}

export class BehaviorCache<
	TKey,
	TResult,
	TCacheSet extends BehaviorCacheSet<TResult> = BehaviorCacheSet<TResult>,
> {
	private _map: Map<TKey | string, TCacheSet>;
	private _keyMap: Map<TKey | string, TKey>;
	public observer: PartialObserver<[set: TCacheSet, data: TResult]>;
	private _keyAdded$ = new Subject<TKey>();
	private secondsUntilStale: number;
	private waitForRefresh: boolean;

	public get keyAdded$() {
		return this._keyAdded$.asObservable();
	}

	public get cacheMap() {
		return this._map;
	}

	constructor(
		private fetch$: (key: TKey) => Observable<TResult>,
		private cacheName: string,
		config?: {
			secondsUntilStale?: number;
			waitForRefresh?: boolean;
		},
	) {
		config = config || {};
		this.secondsUntilStale = config.secondsUntilStale ?? 0;
		this.waitForRefresh = config.waitForRefresh ?? false;

		this._map = new Map<TKey | string, TCacheSet>();
		this._keyMap = new Map<TKey | string, TKey>();
	}

	public getStoredKeys = (): TKey[] => {
		return [...this._keyMap.values()];
	};

	public getStableKey = (key: TKey): TKey | string => {
		if (isArray(key)) {
			return JSON.stringify(key);
		}
		return key;
	};

	public getCacheSet = (key: TKey): TCacheSet => {
		const stableKey = this.getStableKey(key);

		if (!this._map.has(stableKey)) {
			SessionLogger.log(`Cache ${this.cacheName} creating new set for ${key}`);
			this._map.set(stableKey, {
				data: new BehaviorSubject(undefined),
				lastRefresh: null,
			} as TCacheSet);
			this._keyMap.set(stableKey, key);

			this._keyAdded$.next(key);
		}

		return this._map.get(stableKey);
	};

	public getCache = (key: TKey): Observable<Result<TResult>> => {
		const cache = this.getCacheSet(key);
		this.fetchData(key);
		return cache.data.pipe(filter((x) => x !== undefined));
	};

	private _clearData = (stableKey: TKey | string) => {
		const set = this._map.get(stableKey);
		if (set) {
			set.lastRefresh = null;
			set.data.next(undefined);
		}
	};

	public clearData = (key: TKey) => {
		const set = this.getCacheSet(key);
		set.lastRefresh = null;
		set.data.next(undefined);
	};

	public clearAllData = () => {
		for (const k of this._map.keys()) {
			this._clearData(k);
		}
	};

	public tryRefreshData = (key: TKey): Promise<boolean> => {
		const stableKey = this.getStableKey(key);

		if (this._map.has(stableKey)) {
			return this.fetchData(key, true);
		} else {
			return subscribeAndPromise(of(false));
		}
	};

	public fetchData = (key: TKey, forceRefresh?: boolean): Promise<boolean> => {
		const set = this.getCacheSet(key);
		let refresh = set.lastRefresh == null || forceRefresh;
		if (!refresh && this.secondsUntilStale) {
			refresh =
				new Date().getTime() - this.secondsUntilStale * 1000 >
				set.lastRefresh?.getTime();
		}

		if (key == null) {
			set.data.next(Result.err('Invalid key provided'));
			return Promise.resolve(true);
		} else if (refresh) {
			set.lastRefresh = new Date();
			const lastRefresh = set.lastRefresh;

			SessionLogger.log(`Cache ${this.cacheName} fetching for ${key}`);

			if (this.waitForRefresh) {
				set.data.next(undefined);
			}

			return subscribeAndPromise(
				this.fetch$(key).pipe(
					catchError((e) => {
						if (
							e != null &&
							e['_originalResponse'] != null &&
							e['_originalResponse'] instanceof HttpErrorResponse
						) {
							const status = (e._originalResponse as HttpErrorResponse).status;
							if (status >= 500 || status < 100) {
								SessionLogger.log(
									`Cache ${this.cacheName} errored for ${key}, retrying: `,
									e._originalResponse,
								);

								return timer(5000).pipe(
									switchMap(() => {
										if (lastRefresh === set.lastRefresh) {
											return this.fetch$(key);
										} else {
											if (this.observer?.complete) {
												this.observer.complete();
											}

											return NEVER;
										}
									}),
								);
							}
						}

						return throwError(() => e);
					}),
				),
				{
					next: (x) => {
						if (set.lastRefresh != null && lastRefresh !== set.lastRefresh) {
							SessionLogger.log(
								`Cache ${this.cacheName} fetched data for stale request for ${key}, ignoring result`,
								`Expected ${set.lastRefresh?.getTime()}, got ${lastRefresh?.getTime()}`,
							);
							return;
						}

						if (this.observer?.next) {
							this.observer.next([set, x]);
						}

						SessionLogger.log(
							`Cache ${this.cacheName} updated for ${key}: `,
							x,
						);
						set.data.next(Result.ok(x));
					},
					error: (e) => {
						if (set.lastRefresh != null && lastRefresh !== set.lastRefresh) {
							SessionLogger.log(
								`Cache ${this.cacheName} errored for stale request for ${key}, ignoring result`,
								`Expected ${set.lastRefresh?.getTime()}, got ${lastRefresh?.getTime()}`,
							);
							return;
						}

						if (this.observer?.error) {
							this.observer.error([set, e]);
						}

						SessionLogger.log(
							`Cache ${this.cacheName} errored for ${key}: `,
							e,
						);
						console.error(
							`Error while loading ${this.cacheName}. Key `,
							key,
							e,
						);
						set.data.next(Result.err(e));
					},
					complete: () => {
						if (this.observer?.complete) {
							this.observer.complete();
						}
					},
				},
			);
		} else {
			return Promise.resolve(false);
		}
	};
}

export class UnsafeBehaviorCache<TKey, TResult> {
	private _cache: BehaviorCache<TKey, TResult>;

	public get keyAdded$() {
		return this._cache.keyAdded$;
	}

	constructor(
		fetch$: (key: TKey) => Observable<TResult>,
		cacheName: string,
		private emptyValue: (key?: TKey) => TResult = () => null,
		secondsUntilStale: number = null,
		waitForRefresh: boolean = false,
	) {
		this._cache = new BehaviorCache(fetch$, cacheName, {
			secondsUntilStale,
			waitForRefresh,
		});
	}

	public getStoredKeys = (): TKey[] => {
		return this._cache.getStoredKeys();
	};

	public getStableKey = (key: TKey): TKey | string => {
		return this._cache.getStableKey(key);
	};

	public getCache = (key: TKey): Observable<TResult> => {
		return this._cache
			.getCache(key)
			.pipe(map((x) => x.getOrElse(() => this.emptyValue(key))));
	};

	public clearData = (key: TKey) => {
		this._cache.clearData(key);
	};

	public clearAllData = () => {
		this._cache.clearAllData();
	};

	public tryRefreshData = (key: TKey): Promise<boolean> => {
		return this._cache.tryRefreshData(key);
	};

	public fetchData = (key: TKey, forceRefresh?: boolean): Promise<boolean> => {
		return this._cache.fetchData(key, forceRefresh);
	};
}

export function subscribeAndPromise<T>(
	obs$: Observable<T>,
	observer?: PartialObserver<T>,
): Promise<boolean> {
	const subject = new AsyncSubject<boolean>();

	observer = observer ?? ({} as PartialObserver<T>);

	obs$.subscribe({
		next: (t) => {
			if (observer.next) observer.next(t);

			subject.next(true);
			subject.complete();
		},
		error: (e) => {
			if (observer.error) observer.error(e);

			subject.next(false);
			subject.complete();
		},
		complete: () => {
			if (observer.complete) observer.complete();

			subject.complete();
		},
	});

	return firstValueFrom(subject);
}
